import React, { useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiGet } from 'api/service';
import { GREEN, MONO, SEV_COLOR } from './scanUi';
import ScopeGrades from './ScopeGrades';
import { SEVERITIES, gradeOf, sentence, severityBars, whyChips } from './riskLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;

export default function RiskOverviewPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([apiGet('/api/risk-overview/'), apiGet('/api/risk-overview/prioritized/?limit=15')])
      .then(([d, p]) => { setData(d); setQueue(p.results || []); })
      .catch(() => setError('Could not load the risk overview.'))
      .finally(() => setLoading(false));
  }, []);

  const grade = gradeOf(data?.grade);
  const bars = useMemo(() => severityBars(data?.severity), [data]);
  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Risk overview</Typography>
        <Typography color="text.secondary" variant="body2">What to look at first, and why</Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}

      {data && (
        <Stack direction="row" gap={2} flexWrap="wrap" alignItems="stretch">
          <Box sx={{ ...panel, p: 3, flex: '2 1 460px', display: 'flex', gap: 3.5, alignItems: 'center', flexWrap: 'wrap', background: `linear-gradient(135deg, ${alpha(grade.color, 0.1)}, transparent 65%)` }}>
            <Box sx={{ width: 150, height: 150, borderRadius: '50%', display: 'grid', placeItems: 'center', background: `conic-gradient(${grade.color} ${data.score}%, ${alpha('#64748b', 0.25)} 0)` }}>
              <Box sx={{ width: 118, height: 118, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 38, fontWeight: 800, fontFamily: MONO, lineHeight: 1 }}>{data.score}</Typography>
                <Typography variant="caption" color="text.secondary">grade {data.grade}</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 220px' }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Security score</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: grade.color }}>{grade.word}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.8, lineHeight: 1.55 }}>{sentence(data)}</Typography>
              {data.summary.fixed_findings > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{data.summary.fixed_findings} already fixed.</Typography>}
            </Box>
          </Box>
          <Box sx={{ ...panel, p: 2.6, flex: '1 1 300px' }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Open problems</Typography>
            {bars.map((b) => (
              <Stack key={b.id} direction="row" alignItems="center" gap={1.5} sx={{ py: 0.7 }}>
                <Typography variant="body2" sx={{ width: 70, textTransform: 'capitalize' }}>{b.id}</Typography>
                <Box sx={{ flex: 1, height: 9, borderRadius: 5, bgcolor: 'divider' }}><Box sx={{ height: '100%', width: `${b.pct}%`, borderRadius: 5, bgcolor: SEV_COLOR[b.id] }} /></Box>
                <Typography variant="body2" sx={{ width: 30, textAlign: 'right', fontFamily: MONO }} color="text.secondary">{b.n}</Typography>
              </Stack>
            ))}
          </Box>
        </Stack>
      )}
      {data && <ScopeGrades scopes={data.by_scope} />}

      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
        <Box sx={{ ...panel, flex: '2.4 1 560px', overflow: 'hidden' }}>
          <Stack direction="row" justifyContent="space-between" sx={{ p: 2.2 }}>
            <Typography sx={{ fontWeight: 700 }}>Highest risk first</Typography>
            <Typography variant="caption" color="text.secondary">risk points</Typography>
          </Stack>
          {!queue.length && <Typography color="text.secondary" sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>Nothing is open. Well done.</Typography>}
          {queue.map((p, i) => (
            <Stack key={p.finding_id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.6, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13, bgcolor: alpha('#ef4444', 0.14), color: '#f87171' }}>{i + 1}</Box>
              <Box sx={{ flex: '2 1 240px', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>{p.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{p.target_value}{p.cves.length ? ` · ${p.cves.slice(0, 2).join(', ')}` : ''}</Typography>
              </Box>
              <Stack direction="row" gap={0.6} flexWrap="wrap" sx={{ flex: '1 1 160px' }}>
                {whyChips(p).map((c) => <Pill key={c.label} color={c.hot ? '#f87171' : '#94a3b8'}>{c.label.toUpperCase()}</Pill>)}
              </Stack>
              <Pill color={SEV_COLOR[p.severity] || '#64748b'}>{p.severity.toUpperCase()}</Pill>
              <Typography sx={{ width: 40, textAlign: 'right', fontFamily: MONO, fontWeight: 800, fontSize: 16, color: '#fcd34d' }}>{Math.round(p.risk_score)}</Typography>
            </Stack>
          ))}
        </Box>

        <Box sx={{ ...panel, p: 2.6, flex: '1 1 300px' }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Websites, worst first</Typography>
          {!(data?.top_targets || []).length && <Typography color="text.secondary" variant="body2">No open problems on any website.</Typography>}
          {(data?.top_targets || []).map((t) => (
            <Stack key={t.target_id} direction="row" alignItems="center" gap={1.2} sx={{ py: 1.1, borderTop: 1, borderColor: 'divider' }}>
              <Typography sx={{ flex: 1, fontFamily: MONO, fontSize: 13.5, fontWeight: 600, wordBreak: 'break-all' }}>{t.target_value}</Typography>
              <Pill color={SEV_COLOR[t.worst] || '#64748b'}>{t.worst.toUpperCase()}</Pill>
              <Typography variant="caption" color="text.secondary" sx={{ width: 56, textAlign: 'right' }}>{t.findings} open</Typography>
            </Stack>
          ))}
          <Button size="small" color="inherit" onClick={() => nav('/vulns')} sx={{ mt: 1.5, textTransform: 'none' }}>Open Findings</Button>
        </Box>
      </Stack>
      <Typography variant="body2" color="text.secondary">The score and grade count only open problems, with the same rule as the Executive summary: critical 8, high 5, medium 3, low 1. Fixed problems and repeats do not count. The order above also weighs whether attackers are using the flaw right now and how important the website is.</Typography>
    </Stack>
  );
}
